/**
 * Building transfer — bundle format contract.
 *
 * Defines the export ZIP layout, manifest schema, version stamping, the
 * authoritative inventory of user-UID-bearing fields, and the per-user
 * resolution types.
 *
 * Ported from the (unmerged) mf-functions branch `feat/building-transfer`
 * (`functions/src/buildings/transfer/bundleFormat.ts`), which planned this
 * exact mirror location. If that branch is ever revived, the two copies must
 * stay in sync — the drift test in bundleFormat.test.ts pins the load-bearing
 * constants.
 */

/**
 * Bundle layout version. Guards the ZIP structure itself (entry names, shape).
 * A mismatch here blocks import; an `appVersion` mismatch only warns.
 */
export const EXPORT_SCHEMA_VERSION = 1 as const;

/** Fixed entry paths inside the export ZIP. */
export const BUNDLE_PATHS = {
    manifest: 'manifest.json',
    building: 'db/building.json',
    buildingsSummary: 'db/buildingsSummary.json',
    users: 'users/users.json',
    /** Prefix under which raw Storage binaries are stored, mirroring the bucket layout. */
    binariesDir: 'binaries/',
} as const;

/**
 * Subtrees of `buildings/{key}` deliberately left out of the export
 * (decided 2026-09-01): timeline events are history, todos and external
 * contacts are people-tied noise in a test/demo instance. The matching
 * Storage prefixes are skipped too.
 */
export const EXCLUDED_BUILDING_SUBTREES = ['timelineItems', 'todos', 'externalContacts'] as const;

/**
 * Storage prefixes a bundle may contain binaries under. Cover photos live
 * under `buildingsSummary/{key}/` (not `buildings/{key}/`), so both prefixes
 * are part of the format contract — for export collection and for the import
 * path confinement alike.
 */
export const bundleStoragePrefixes = (buildingKey: string): string[] => [
    `buildings/${buildingKey}/`,
    `buildingsSummary/${buildingKey}/`,
];

/**
 * User-UID-bearing **scalar** fields that occur within a building's exported
 * subtree. The import remapper rewrites any property whose key is in this set
 * and whose value equals the source UID being remapped. Grounded in the
 * building data model (`src/types/protocols.tsx`, `buildings.ts`, `todos.ts`):
 *
 *  - author         PhotoModel (component/room/level photos, mapImages, blueprints)
 *  - createdBy      BaseComponent, TodoItem
 *  - updatedBy      every ValueWithMetadata<T> field (components and rooms)
 *  - addedBy        BuildingExternalContact
 *  - uploadedBy     ComponentDocument, building DocumentModel, FileAttachment
 *  - archivedBy     BaseComponent
 *  - completedBy    TodoItem
 *  - lockedBy       BuildingSummary.offlineLock
 *  - externalUserId BuildingExternalContact (note: ALSO the node key)
 *
 * Maintained as one constant so export-time UID collection, the import preview,
 * and remap rewriting never diverge.
 */
export const UID_SCALAR_FIELDS = [
    'author',
    'createdBy',
    'updatedBy',
    'addedBy',
    'uploadedBy',
    'archivedBy',
    'completedBy',
    'lockedBy',
    'externalUserId',
] as const;

/**
 * Array-of-reference fields deliberately EXCLUDED from user resolution because
 * they hold group identifiers, not user UIDs.
 */
export const UID_ARRAY_FIELDS_EXCLUDED = ['responsibleGroups'] as const;

export type UidScalarField = (typeof UID_SCALAR_FIELDS)[number];

/** A referenced user as carried in the export — NAME ONLY, never email/PII/privileges. */
export interface ExportedUser {
    name: string;
}

/** users/users.json payload: referenced source UID → exported (name-only) record. */
export type ExportedUsers = Record<string, ExportedUser>;

export interface BuildingExportCounts {
    components: number;
    binaries: number;
    totalBytes: number;
    referencedUsers: number;
}

/** manifest.json schema. */
export interface BuildingExportManifest {
    exportSchemaVersion: typeof EXPORT_SCHEMA_VERSION;
    /** Web app version at export time — mismatch warns at import, does not block. */
    appVersion: string;
    exportedFrom: {
        projectId: string;
        instanceName?: string;
    };
    /** Stockholm `YYYY-MM-DD hh:mm:ss`. */
    exportedAt: string;
    /** UID of the operator who ran the export. */
    exportedBy: string;
    buildingKey: string;
    buildingName: string;
    counts: BuildingExportCounts;
    /** Convenience index for the import preview; names mirror users/users.json. */
    referencedUsers: Array<{ uid: string; name: string }>;
    /** Storage paths referenced in the DB but outside buildings/{key}/ — not bundled. */
    skippedStoragePaths?: string[];
}

/** Per-user import resolution. */
export type UserResolutionStrategy = 'copy' | 'remap' | 'leave';

export interface UserResolutionEntry {
    strategy: UserResolutionStrategy;
    /** Required when strategy === "remap": the existing target-env UID to point references at. */
    targetUid?: string;
}

/** Resolution map keyed by SOURCE uid. */
export type UserResolution = Record<string, UserResolutionEntry>;
